import { Category } from "@common/types/category";
import { Form, FormikProvider, useFormik } from "formik";
import { useEffect, useMemo, useRef } from "react";
import { z } from "zod";
import { createCategoryBodySchema } from "@common/schemas/category.schema";
import { Picker } from "@design-system/Picker/Picker.lib";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import {
    useCreateCategoryWithGlMappingsMutation,
    useCreateCustomCategoryMutation,
} from "@api/category.api";
import { useCategoriesTree } from "@hooks/useCategoriesTree";
import { useIsCategoryGlMappingEnabled } from "@hooks/useIsCategoryGlMappingEnabled";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { Button } from "@design-system/Button/Button";
import { StandardModalHeader } from "@design-system/Modal/Modal";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { CategoryIcon } from "../../transactions/CategoryIcon";
import {
    CategoryGlMappingGrid,
    useCategoryGlMappingGrid,
    type CategoryGlMappingOverridePayload,
} from "./CategoryGlMappingGrid";
import {
    CreateCategoryDefaultFooter,
    CreateCategoryGridFooter,
} from "./CreateCategoryGridFooter";
import { NON_BUSINESS_TAXONOMIES } from "./categoryGlGridEligibility";
import styles from "./CreateCategoryModal.module.scss";
import { createRulesUrl } from "@components/settings/Rules/lib/rulesUrl";
import { RuleGroupType } from "@common/types/transactionRule";

type CreateCategoryFormSchema = z.infer<typeof createCategoryBodySchema>;

const fields = createFormikFields<CreateCategoryFormSchema>();

interface CreateCategoryModalProps {
    onHide: () => void;
    parentCategoryId?: string;
    categoryName?: string;
    onCategoryCreated?: (category: Category) => void;
}

function useTopLevelCategoryData(parentCategoryIdProp: string | undefined) {
    const categoriesTree = useCategoriesTree();
    const topLevelCategories = useMemo(
        () => categoriesTree.filter((c) => c.parentCategoryId === null),
        [categoriesTree],
    );
    const hasTopLevelCategories = topLevelCategories.length > 0;

    const initialParentCategoryId = useMemo(() => {
        if (!hasTopLevelCategories) {
            return "";
        }
        if (parentCategoryIdProp == null) {
            return topLevelCategories[0].id;
        }
        const isTopLevel = topLevelCategories.some(
            (c) => c.id === parentCategoryIdProp,
        );
        return isTopLevel
            ? parentCategoryIdProp
            : (topLevelCategories[0]?.id ?? parentCategoryIdProp);
    }, [parentCategoryIdProp, topLevelCategories, hasTopLevelCategories]);

    return {
        topLevelCategories,
        hasTopLevelCategories,
        initialParentCategoryId,
    };
}

const CreateCategoryEmptyState = ({
    onHide,
}: Readonly<{ onHide: () => void }>) => (
    <>
        <StandardModalHeader>Create subcategory</StandardModalHeader>
        <p className="mb-0">
            No parent categories available. Categories may still be loading.
        </p>
        <HStack as="footer" justify="end" gap={12} className="mt-3">
            <Button
                variant="secondary"
                size="xl"
                type="button"
                onClick={onHide}
            >
                Cancel
            </Button>
        </HStack>
    </>
);

export const CreateCategoryModal = ({
    parentCategoryId: parentCategoryIdProp,
    categoryName,
    onCategoryCreated,
    onHide,
}: CreateCategoryModalProps) => {
    const navigate = useStateAwareNavigate();
    const createMutation = useCreateCustomCategoryMutation();
    const createWithGlMappingsMutation =
        useCreateCategoryWithGlMappingsMutation();
    const {
        topLevelCategories,
        hasTopLevelCategories,
        initialParentCategoryId,
    } = useTopLevelCategoryData(parentCategoryIdProp);

    const isFlagOn = useIsCategoryGlMappingEnabled();

    const categoryItems = useMemo(
        () =>
            topLevelCategories.map((category) => ({
                value: category.id,
                label: category.label,
                leading: <CategoryIcon categoryId={category.id} />,
            })),
        [topLevelCategories],
    );

    const isGridEnabledRef = useRef(false);
    const shouldCustomizeRef = useRef(false);
    const getOverridesRef = useRef<() => CategoryGlMappingOverridePayload[]>(
        () => [],
    );

    const formik = useFormik<CreateCategoryFormSchema>({
        initialValues: {
            label: categoryName ?? "",
            description: "",
            parentCategoryId: initialParentCategoryId,
        },
        enableReinitialize: true,
        validate: validateFormikWithZodSchema(createCategoryBodySchema),
        onSubmit: submitHelper({
            loading:
                createMutation.isPending ||
                createWithGlMappingsMutation.isPending,
            handler: async (data) => {
                if (isGridEnabledRef.current) {
                    const created =
                        await createWithGlMappingsMutation.mutateAsync({
                            parentCategoryId: data.parentCategoryId,
                            label: data.label,
                            description: data.description ?? null,
                            overrides: getOverridesRef.current(),
                        });
                    onCategoryCreated?.(created);
                    if (shouldCustomizeRef.current) {
                        navigate(
                            createRulesUrl({
                                tab: RuleGroupType.ACCOUNTING,
                                filters: { categoryId: created.id },
                            }),
                        );
                    }
                    onHide();
                    return;
                }

                const createdCategory = await createMutation.mutateAsync({
                    description: data.description ?? null,
                    label: data.label,
                    parentCategoryId: data.parentCategoryId,
                });
                onCategoryCreated?.(createdCategory);
                onHide();
            },
        }),
        validateOnMount: true,
    });

    const selectedParentCategory = useMemo(
        () =>
            topLevelCategories.find(
                (c) => c.id === formik.values.parentCategoryId,
            ),
        [topLevelCategories, formik.values.parentCategoryId],
    );

    const isBusinessParent =
        selectedParentCategory != null &&
        !NON_BUSINESS_TAXONOMIES.includes(selectedParentCategory.taxonomy);
    const isGridEnabled = isFlagOn && isBusinessParent;

    const grid = useCategoryGlMappingGrid({
        parentCategoryId: formik.values.parentCategoryId,
        isEnabled: isGridEnabled,
    });

    useEffect(() => {
        isGridEnabledRef.current = isGridEnabled;
        getOverridesRef.current = grid.toOverridesPayload;
    }, [isGridEnabled, grid.toOverridesPayload]);

    const submitForm = () => {
        void formik.submitForm().finally(() => {
            shouldCustomizeRef.current = false;
        });
    };

    const handleAddSubcategory = () => {
        shouldCustomizeRef.current = false;
        submitForm();
    };
    const handleAddAndCustomizeRule = () => {
        shouldCustomizeRef.current = true;
        submitForm();
    };

    if (!hasTopLevelCategories) {
        return <CreateCategoryEmptyState onHide={onHide} />;
    }

    return (
        <>
            <StandardModalHeader>Create subcategory</StandardModalHeader>
            <FormikProvider value={formik}>
                <Form onSubmit={formik.handleSubmit}>
                    <VStack gap={16} className={styles.fields}>
                        <FormikField
                            name="parentCategoryId"
                            label="Parent category"
                            control="button"
                        >
                            {() => (
                                <Picker.Select
                                    items={categoryItems}
                                    searchable
                                    value={selectedParentCategory?.id ?? null}
                                    onValueChange={(id) => {
                                        if (id !== null) {
                                            void formik.setFieldValue(
                                                "parentCategoryId",
                                                id,
                                            );
                                        }
                                    }}
                                    renderValue={() =>
                                        selectedParentCategory ? (
                                            <span className={styles.trigger}>
                                                <CategoryIcon
                                                    categoryId={
                                                        selectedParentCategory.id
                                                    }
                                                />
                                                <span>
                                                    {
                                                        selectedParentCategory.label
                                                    }
                                                </span>
                                            </span>
                                        ) : undefined
                                    }
                                    placeholder="Select parent category"
                                    searchPlaceholder="Search..."
                                    emptyLabel="No categories"
                                    trigger={
                                        <Picker.Button
                                            variant="primary"
                                            size="xl"
                                            block
                                        />
                                    }
                                />
                            )}
                        </FormikField>
                        <VStack gap={16}>
                            <fields.Text name="label" label="Name" size="xl" />
                            <fields.Text
                                name="description"
                                label="Description"
                                size="xl"
                            />
                        </VStack>
                    </VStack>
                    {isGridEnabled && <CategoryGlMappingGrid grid={grid} />}
                    {isGridEnabled ? (
                        <CreateCategoryGridFooter
                            isLoading={createWithGlMappingsMutation.isPending}
                            onCancel={onHide}
                            onAddAndCustomize={handleAddAndCustomizeRule}
                            onAddSubcategory={handleAddSubcategory}
                        />
                    ) : (
                        <CreateCategoryDefaultFooter
                            isLoading={createMutation.isPending}
                            isValid={formik.isValid}
                            hasAssignSuffix={onCategoryCreated != null}
                            onCancel={onHide}
                        />
                    )}
                </Form>
            </FormikProvider>
        </>
    );
};
